import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageTitle, Card, GBtn, Field, inputCls, StatusBadge } from "./ui";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Send, Eye, Save } from "lucide-react";

const TEMPLATE_LABELS = {
  order_thankyou: "Order thank-you", questionnaire_link: "Questionnaire link",
  questionnaire_reminder: "Questionnaire reminder", questionnaire_received: "Questionnaire received",
  delivery: "Delivery email", enquiry_autoreply: "Enquiry auto-reply", free_guide: "Free guide email",
};

export default function EmailCentre() {
  const [emails, setEmails] = useState([]);
  const [view, setView] = useState(null);
  const [send, setSend] = useState({ to: "", subject: "", message: "" });
  const [templates, setTemplates] = useState(null);

  const load = () => api.get("/admin/emails").then((r) => setEmails(r.data));
  useEffect(() => {
    load();
    api.get("/admin/email-templates").then((r) => setTemplates(r.data));
  }, []);

  const sendEmail = async () => {
    try { await api.post("/admin/send-email", send); toast.success("Email sent"); setSend({ to: "", subject: "", message: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveTemplates = async () => {
    await api.put("/admin/email-templates", templates); toast.success("Templates saved");
  };
  const setTpl = (k, field, v) => setTemplates((t) => ({ ...t, [k]: { ...t[k], [field]: v } }));

  return (
    <>
      <PageTitle title="Email Centre" subtitle="Logs, sending & templates" />
      <Tabs defaultValue="log">
        <TabsList className="bg-black/40 border border-white/10">
          <TabsTrigger value="log" data-testid="tab-log">Email log</TabsTrigger>
          <TabsTrigger value="send" data-testid="tab-send">Send email</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="log" className="mt-4">
          <div className="card-surface rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead><tr className="border-b border-white/10 text-white/50 text-xs uppercase">
                <th className="text-left p-4">To</th><th className="text-left p-4">Subject</th><th className="text-left p-4">Type</th>
                <th className="text-left p-4">Status</th><th className="text-left p-4">Date</th><th className="p-4" />
              </tr></thead>
              <tbody>
                {emails.map((e) => (
                  <tr key={e.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4 max-w-[180px] truncate">{e.to}</td>
                    <td className="p-4 max-w-[220px] truncate">{e.subject}</td>
                    <td className="p-4 text-white/50 text-xs">{e.type}</td>
                    <td className="p-4"><StatusBadge status={e.status} /></td>
                    <td className="p-4 text-white/40 text-xs">{e.created_at ? new Date(e.created_at).toLocaleString("en-GB") : ""}</td>
                    <td className="p-4"><button onClick={() => setView(e)} className="text-[#D4AF37]"><Eye size={16} /></button></td>
                  </tr>
                ))}
                {emails.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-white/30">No emails yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="send" className="mt-4">
          <Card className="max-w-2xl space-y-4">
            <p className="text-xs text-white/50">You can email existing customers, subscribers or enquirers. A branded template is applied automatically.</p>
            <Field label="Recipient email"><input value={send.to} onChange={(e) => setSend({ ...send, to: e.target.value })} data-testid="send-to" className={inputCls} /></Field>
            <Field label="Subject"><input value={send.subject} onChange={(e) => setSend({ ...send, subject: e.target.value })} data-testid="send-subject" className={inputCls} /></Field>
            <Field label="Message"><textarea rows={6} value={send.message} onChange={(e) => setSend({ ...send, message: e.target.value })} data-testid="send-message" className={inputCls} /></Field>
            <GBtn onClick={sendEmail} data-testid="send-email-btn"><Send size={16} /> Send email</GBtn>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          {templates && (
            <div className="space-y-4">
              <GBtn onClick={saveTemplates} data-testid="save-templates"><Save size={16} /> Save all templates</GBtn>
              {Object.keys(templates).map((k) => (
                <Card key={k}>
                  <h3 className="font-display text-lg text-[#D4AF37] mb-3">{TEMPLATE_LABELS[k] || k}</h3>
                  <Field label="Subject"><input value={templates[k].subject} onChange={(e) => setTpl(k, "subject", e.target.value)} className={inputCls} /></Field>
                  <div className="mt-3"><Field label="Body (HTML — placeholders like {name} allowed)"><textarea rows={4} value={templates[k].body} onChange={(e) => setTpl(k, "body", e.target.value)} className={`${inputCls} font-mono text-xs`} /></Field></div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="bg-[#111] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{view?.subject}</DialogTitle></DialogHeader>
          <div className="text-sm text-white/50 mb-3">To: {view?.to} · {view?.status}{view?.error ? ` · ${view.error}` : ""}</div>
          <div className="rounded-lg overflow-hidden border border-white/10 bg-white">
            <iframe title="email" srcDoc={view?.html} className="w-full h-[500px]" />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
